import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChipsModule } from 'primeng/chips';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { APP_ROUTING } from '../../app-routing.constants';
import { AuthService } from '../../login-service/auth-service';
import { Categories } from '../../models/categories';
import { QuestionBank } from '../../models/questionBank';
import { CrudService } from '../../services/crud-app.service';
import { ErrorCommunicationService } from '../../services/error-communication.service';
import { environment } from '../../../environments/environment';
import { EditorImageHandlerService } from '../../services/editor-image-handler.service';
import { QUESTION_TYPES, ALLOWED_QUESTION_TYPES } from '../../exam.constants';

interface OptionMultiSelect {
  optionName: string;
  code: string;
}

@Component({
  selector: 'app-question-edit',
  standalone: true,
  imports: [
    FormsModule,
    DropdownModule,
    MultiSelectModule,
    CommonModule,
    ChipsModule,
    CardModule,
    EditorModule,
    ButtonModule,
    DialogModule,
    TagModule,
  ],
  templateUrl: './question-edit.component.html',
  styleUrl: './question-edit.component.scss',
  providers: [],
})
export class QuestionEditComponent implements OnInit, AfterViewInit {
  readonly QUESTION_TYPES = QUESTION_TYPES;
  id = '';
  question: QuestionBank = {} as QuestionBank;
  options: unknown[] = [];
  questionCategories: Categories[] = [];
  loading = false;
  isDuplicate = false;
  visible = false;
  optionMultiSelect: OptionMultiSelect[] = [];
  selectedMultiSelect: OptionMultiSelect[] = [];
  showDuplicateBtn = true;
  category: string[] = [];

  subjects: { id: string; name: string }[] = [];
  topicsForSelectedSubject: { id: string; name: string }[] = [];
  selectedSubjectId: string | null = null;
  selectedTopicIds: string[] = [];

  isFromExam = false;
  userId = '';
  @ViewChild('fullTextEditor') fullTextEditor: any;
  @ViewChild('solutionEditor') solutionEditor: any;
  titleError: any;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private crud: CrudService,
    private route: ActivatedRoute,
    private router: Router,
    private errorCommunicationService: ErrorCommunicationService,
    private authService: AuthService,
    public cdr: ChangeDetectorRef,
    private editorImageHandlerService: EditorImageHandlerService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.userId = this.authService.userId() ?? '';
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.isDuplicate = this.route.snapshot.queryParamMap.get('duplicate') === 'true';
    this.isFromExam = this.route.snapshot.queryParamMap.get('from') === 'exam';

    try {
      // load categories and subject topics in parallel
      const [categories] = await Promise.all([this.crud.get('question-category')]);
      const allCategories = (categories as Categories[]) ?? [];
      // Filter to allowed types and remove duplicates
      const seen = new Set<string>();
      this.questionCategories = allCategories.filter((cat) => {
        if (!ALLOWED_QUESTION_TYPES.includes(cat.code) || seen.has(cat.code)) {
          return false;
        }
        seen.add(cat.code);
        return true;
      });

      if (this.id !== '0') {
        await this.loadQuestion(this.id);
      } else {
        this.showDuplicateBtn = false;
        this.openNew();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      this.loading = false;
    } finally {
      await this.loadSubjectTopics();
      await this.initializeSubjectAndTopicsFromQuestion();
    }

    if (isPlatformBrowser(this.platformId)) {
      this.openWarning();
    }
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  onEditorInit(): void {
    // Initialize image handlers for both editors
    this.editorImageHandlerService.initializeImageHandlers(this.solutionEditor, this.cdr);
    this.editorImageHandlerService.initializeImageHandlers(this.fullTextEditor, this.cdr);
  }

  onImageUpload(event: any): void {
    this.editorImageHandlerService.validateUploadedImage(event);
  }

  private async loadQuestion(id: string): Promise<void> {
    this.loading = true;
    try {
      const q = (await this.crud.get('questions/id/' + id)) as any;
      this.question = q as QuestionBank;

      if (q?.category?.code) {
        this.question.category = q.category.code;
        this.question.categoryId = q.category.id;
      }

      if (this.isFromExam) {
        this.showDuplicateBtn = true;
      }
      if (this.isDuplicate) {
        this.question.title = (this.question.title ?? '') + ' - copy';
        this.showDuplicateBtn = false;
      }

      this.question.isUpdate = !this.isDuplicate && !this.isFromExam;
      this.options = Object.values(this.question.options ?? {});
      this.setQuestionCategoryConfig();

      // prepare multi-select options if needed
      if (this.question.category === QUESTION_TYPES.MCQ_MULTIPLE_CORRECT) {
        this.prepareMultiSelectOptions();
        // restore previously selected options if answerOption has comma-separated letters
        if (this.question.answerOption) {
          const selectedCodes = String(this.question.answerOption)
            .split(',')
            .map((s) => s.trim());
          this.selectedMultiSelect = this.optionMultiSelect.filter((o) =>
            selectedCodes.includes(o.code),
          );
        }
      }

      // Fill in the Blank: API may return answerOption as the text value; ensure options['A'] has it and dropdown shows 'A'
      if (this.question.category === QUESTION_TYPES.FILL_IN_BLANK) {
        const opts = (this.question.options ?? {}) as Record<string, string>;
        const answerVal = this.question.answerOption?.trim();
        if (answerVal && opts['A'] !== answerVal) {
          this.question.options = { ...opts, A: answerVal };
        }
        if (!opts['A'] && answerVal) {
          this.question.options = { A: answerVal };
        }
        this.question.answerOption = 'A';
      }
    } finally {
      this.loading = false;
    }
  }

  async loadSubjectTopics(): Promise<void> {
    try {
      const courseId = this.authService.getSelectedCourseId();
      const options = courseId ? { params: { courseId: courseId } } : undefined;
      const subjects = await this.crud.get('topics/subjects', options);
      this.subjects = (subjects ?? []).map((s: any) => ({
        id: s.id,
        name: s.name,
      }));
    } catch (error) {
      console.error('Error fetching subjects:', error);
      this.loading = false;
    }
  }

  async onSubjectChange(): Promise<void> {
    if (!this.selectedSubjectId) {
      this.topicsForSelectedSubject = [];
      this.selectedTopicIds = [];
      return;
    }

    try {
      const subtopics = await this.crud.get(`topics/subtopics?subjectId=${this.selectedSubjectId}`);
      this.topicsForSelectedSubject = (subtopics ?? []).map((s: any) => ({
        id: s.id,
        name: s.name,
      }));

      const allowedIds = new Set(this.topicsForSelectedSubject.map((t) => t.id));
      this.selectedTopicIds = (this.selectedTopicIds ?? []).filter((id) => allowedIds.has(id));
    } catch (error) {
      console.error('Error fetching subtopics for subject:', error);
      this.topicsForSelectedSubject = [];
      this.selectedTopicIds = [];
    }
  }

  openWarning() {
    this.visible = this.isDuplicate;
  }

  openNew() {
    this.question = {
      id: '',
      title: '',
      fullText: '',
      category: '',
      categoryId: '',
      tags: [],
      options: {},
      numberOfOption: 0,
      answerOption: '',
      solution: '',
      level: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      isNew: false,
      isUpdate: false,
      userId: this.userId,
    };
    this.selectedMultiSelect = [];
    this.optionMultiSelect = [];
  }

  private async initializeSubjectAndTopicsFromQuestion(): Promise<void> {
    if (this.id === '0') {
      return;
    }

    const tags = this.question.tags ?? [];
    if (!Array.isArray(tags) || tags.length === 0) {
      return;
    }

    const subject = this.subjects.find((s) =>
      (this.question.tags ?? []).some((tag: string) => tag.toLowerCase() === s.name.toLowerCase()),
    );
    if (!subject) {
      return;
    }

    this.selectedSubjectId = subject.id;

    let remainingTags = (this.question.tags ?? []).filter(
      (tag: string) => tag.toLowerCase() !== subject.name.toLowerCase(),
    );

    try {
      const subtopics = await this.crud.get(`topics/subtopics?subjectId=${subject.id}`);
      this.topicsForSelectedSubject = (subtopics ?? []).map((s: any) => ({
        id: s.id,
        name: s.name,
      }));

      const topicNameToId = new Map(
        this.topicsForSelectedSubject.map((t) => [t.name.toLowerCase(), t.id] as [string, string]),
      );

      this.selectedTopicIds = [];
      remainingTags = remainingTags.filter((tag: string) => {
        const topicId = topicNameToId.get(tag.toLowerCase());
        if (topicId) {
          this.selectedTopicIds.push(topicId);
          return false;
        }
        return true;
      });

      this.question.tags = remainingTags;
    } catch (error) {
      console.error('Error initializing subject/topics from question tags:', error);
    }
  }

  isObjectComplete(obj: any): boolean {
    return Object.entries(obj ?? {}).every(([key, value]) => {
      // fields that are allowed to be missing/ignored
      if (['createdAt', 'updatedAt', 'id', 'isNew', 'isUpdate', 'userId'].includes(key))
        return true;
      if (value === '' || value === undefined || value === null) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) {
        if (this.question.category === QUESTION_TYPES.SUBJECTIVE) {
          return true;
        }
        return false;
      }
      return true;
    });
  }

  changeQuestionCategory() {
    // find category config
    this.setQuestionCategoryConfig();

    // build options according to numberOfOption
    const jsonObject: Record<string, string> = {};
    for (let i = 0; i < (this.question.numberOfOption ?? 0); i++) {
      jsonObject[String.fromCharCode(65 + i)] = '';
    }
    this.question.options = jsonObject;
    this.question.answerOption = 'A';

    if (this.question.category === QUESTION_TYPES.MCQ_MULTIPLE_CORRECT) {
      this.prepareMultiSelectOptions();
    } else {
      this.optionMultiSelect = [];
      this.selectedMultiSelect = [];
    }
  }

  private prepareMultiSelectOptions() {
    const n = Math.min(Math.max(this.question.numberOfOption ?? 4, 1), 26); // limit 1..26
    this.optionMultiSelect = Array.from({ length: n }, (_, i) => {
      const code = String.fromCharCode(65 + i);
      return { optionName: code, code };
    });
    // keep selected filtered to available options
    if (this.selectedMultiSelect?.length) {
      const allowed = new Set(this.optionMultiSelect.map((o) => o.code));
      this.selectedMultiSelect = this.selectedMultiSelect.filter((s) => allowed.has(s.code));
    } else {
      this.selectedMultiSelect = [];
    }
  }

  onOptionchange(key: string, event: any) {
    const newValue = event?.target?.value ?? event ?? '';
    if (!this.question.options) this.question.options = {};
    (this.question.options as any)[key] = newValue;
  }

  onTagchange() {
    // placeholder for tag change handling (kept for template compatibility)
  }

  getSubjectName(): string {
    if (!this.selectedSubjectId) {
      return '';
    }
    const subject = this.subjects.find((s) => s.id === this.selectedSubjectId);
    return subject?.name ?? '';
  }

  private checkNavigationContext(): any {
    const navigationContext = sessionStorage.getItem('examNavigationContext');
    if (!navigationContext) return null;
    try {
      return JSON.parse(navigationContext);
    } catch (error) {
      console.error('Invalid examNavigationContext in sessionStorage', error);
      sessionStorage.removeItem('examNavigationContext');
      return null;
    }
  }

  private navigateBackToExamConfig(context: any): void {
    const examStepContext = {
      targetStep: context.currentStep || 1,
      sectionId: context.sectionId,
      restoreFromQuestionEdit: true,
    };
    sessionStorage.setItem('examStepContext', JSON.stringify(examStepContext));
    sessionStorage.removeItem('examNavigationContext');
    this.router.navigate([APP_ROUTING['EXAM_APP'], context.examId]);
  }

  async save(): Promise<void> {
    // Upload all base64 images to server and replace with server URLs
    this.question.fullText = await this.editorImageHandlerService.uploadBase64Images(
      this.question.fullText,
    );
    this.question.solution = await this.editorImageHandlerService.uploadBase64Images(
      this.question.solution,
    );

    if ((this.question.title || '').length > 50) {
      this.errorCommunicationService.showError('Title cannot exceed 50 characters');
      return;
    }

    // Handle MCQ - Multiple Correct category
    if (this.question.category === QUESTION_TYPES.MCQ_MULTIPLE_CORRECT) {
      const selected = (this.selectedMultiSelect ?? []).map((s) => s.optionName).join(',');
      this.question.answerOption = selected;

      if (!selected) {
        this.errorCommunicationService.showError(
          'Please select at least one correct option for Multiple Correct questions.',
        );
        return;
      }
    }

    // Handle Fill in the Blank: send the value entered in the option as both options and answerOption
    if (this.question.category === QUESTION_TYPES.FILL_IN_BLANK) {
      const optionValue = (this.question.options as Record<string, string>)?.['A'] ?? '';
      this.question.answerOption = optionValue;
    }

    if (!this.isObjectComplete(this.question)) {
      this.errorCommunicationService.showError(
        'Please complete all required fields in the form to proceed.',
      );
      return;
    }

    const now = new Date();
    this.question.createdAt = now;
    this.question.updatedAt = now;

    const tagNames: string[] = [];

    if (this.selectedSubjectId) {
      const subject = this.subjects.find((s) => s.id === this.selectedSubjectId);
      if (subject) {
        tagNames.push(subject.name);
      }
    }

    const topicNameById = new Map(
      this.topicsForSelectedSubject.map((t) => [t.id, t.name] as [string, string]),
    );

    for (const topicId of this.selectedTopicIds ?? []) {
      const name = topicNameById.get(topicId);
      if (name) {
        tagNames.push(name);
      }
    }

    this.question.tags = Array.from(new Set([...(this.question.tags ?? []), ...tagNames]));

    this.question.userId = this.userId;

    const { numberOfOption, id, isUpdate, category, ...cleanQuestion } = this.question as any;

    try {
      const apiEndpoint = this.question.isUpdate ? `questions/${this.id}` : 'questions';
      const apiCall = this.question.isUpdate
        ? this.crud.update(apiEndpoint, cleanQuestion)
        : this.crud.post(apiEndpoint, cleanQuestion);

      await apiCall;

      const navigationContext = this.checkNavigationContext();
      this.router.navigate(
        navigationContext?.fromExamConfig
          ? [APP_ROUTING['EXAM_APP'], navigationContext.examId]
          : [APP_ROUTING['QUESTION_BANK']],
      );

      if (navigationContext?.fromExamConfig) {
        sessionStorage.setItem(
          'examStepContext',
          JSON.stringify({
            targetStep: navigationContext.currentStep || 1,
            sectionId: navigationContext.sectionId,
            restoreFromQuestionEdit: true,
          }),
        );
        sessionStorage.removeItem('examNavigationContext');
      }
    } catch (error) {
      console.error('Error saving question:', error);
    }
  }

  private setQuestionCategoryConfig(): void {
    const cat = this.questionCategories.find((c) => c.code === this.question.category);
    if (cat) {
      this.question.numberOfOption = cat.numberOfOptions;
      this.question.categoryId = cat.id;
    }
  }

  cancel() {
    const assignmentContext = sessionStorage.getItem('assignmentNavigationContext');

    if (assignmentContext) {
      try {
        const context = JSON.parse(assignmentContext);
        this.router.navigate([APP_ROUTING['EDIT_ASSIGNMENT'], context.assignmentId]);
        return;
      } catch (error) {
        console.error('Invalid assignmentNavigationContext in sessionStorage', error);
        sessionStorage.removeItem('assignmentNavigationContext');
        // fall through to the exam-context / question-bank fallback below
      }
    }

    const navigationContext = this.checkNavigationContext();
    if (navigationContext && navigationContext.fromExamConfig) {
      this.navigateBackToExamConfig(navigationContext);
    } else {
      this.router.navigate([APP_ROUTING['QUESTION_BANK']]);
    }
  }

  duplicate() {
    if ((this.question.title || '').length > 50) {
      this.errorCommunicationService.showError('Title cannot exceed 50 characters');
      return;
    }
    if (!this.isObjectComplete(this.question)) {
      this.errorCommunicationService.showError(
        'Please complete all required fields in the form to proceed.',
      );
      return;
    }
    this.question.isUpdate = false;
    this.save();
  }
}
